/**
 * BookingPrint — printable / PDF-friendly single-booking detail view.
 *
 * Opened via the "Print" button in the booking form dialog. Renders the
 * full booking record in a compact A4-friendly layout with clean
 * typography and no chrome. `window.print()` is auto-triggered on load
 * so the browser's native print dialog appears immediately; users can
 * pick "Save as PDF", "Print", or dismiss.
 *
 * Route: /bookings/:id/print — auth-gated by ProtectedRoute in App.js.
 *
 * Data: single Supabase select on lbs_bookings + tiny joins for site,
 * bay and public link (for reference display only). No mutations here.
 */
import { createContext, useContext, useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { supabase } from "@/lib/supabaseClient";
import { listBookingActivity } from "@/lib/api";
import { BOOKING_STATUSES } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Printer } from "lucide-react";
import { ALL_VISIBLE, useFormVisibility } from "@/lib/formVisibility";
import { DeclarationsSnapshot, ArrivalNotice, useDeclarationsNotice } from "@/components/DeclarationsBlock";
import { outstandingFromSnapshot } from "@/lib/api";

const NOT_SET = "—";

function fmtDateTime(iso, tz) {
  if (!iso) return NOT_SET;
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-AU", {
      timeZone: tz || undefined,
      weekday: "short", day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit", hour12: false,
    });
  } catch { return iso; }
}

function fmtDate(iso, tz) {
  if (!iso) return NOT_SET;
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("en-AU", {
      timeZone: tz || undefined,
      weekday: "short", day: "2-digit", month: "short", year: "numeric",
    });
  } catch { return iso; }
}

function fmtRegos(v) {
  if (!v) return NOT_SET;
  if (typeof v === "string") return v || NOT_SET;
  if (Array.isArray(v)) return v.filter(Boolean).join(", ") || NOT_SET;
  if (typeof v === "object") {
    const parts = [];
    if (v.prime_mover) parts.push(`Prime mover: ${v.prime_mover}`);
    if (v.trailer_1)   parts.push(`Trailer 1: ${v.trailer_1}`);
    if (v.trailer_2)   parts.push(`Trailer 2: ${v.trailer_2}`);
    // catch-all for other keys
    Object.entries(v).forEach(([k, val]) => {
      if (!["prime_mover","trailer_1","trailer_2"].includes(k) && val) {
        parts.push(`${k}: ${val}`);
      }
    });
    return parts.length ? parts.join(" · ") : NOT_SET;
  }
  return String(v);
}

const VisCtx = createContext(ALL_VISIBLE);

// fk = form field key, slot = slot key: hidden/renamed per tenant Field Designer.
function Field({ label, children, wide = false, fk, slot }) {
  const vis = useContext(VisCtx);
  if (fk && !vis.fieldVisible(fk)) return null;
  if (slot && !vis.slotVisible(slot)) return null;
  if (fk) label = vis.fieldLabel(fk, label);
  return (
    <div className={wide ? "col-span-2" : ""}>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-medium">{label}</div>
      <div className="text-sm text-slate-900 mt-0.5 break-words">
        {children || NOT_SET}
      </div>
    </div>
  );
}

function Section({ title, children, tab }) {
  const vis = useContext(VisCtx);
  if (tab && !vis.tabVisible(tab)) return null;
  return (
    <section className="mb-6 avoid-break">
      <h2 className="text-[11px] uppercase tracking-widest text-slate-700 font-semibold border-b border-slate-300 pb-1 mb-3">
        {title}
      </h2>
      <div className="grid grid-cols-2 gap-x-6 gap-y-3">{children}</div>
    </section>
  );
}

function DeclarationsSection({ booking }) {
  const notice = useDeclarationsNotice({ siteId: booking.site_id });
  const items = Array.isArray(booking.declarations) ? booking.declarations : [];
  if (!items.length) return null;
  const pending = outstandingFromSnapshot(items);
  return (
    <section className="mb-6 avoid-break" data-testid="print-declarations-section">
      <h2 className="text-[11px] uppercase tracking-widest text-slate-700 font-semibold border-b border-slate-300 pb-1 mb-3">
        Driver declarations
      </h2>
      {pending.length > 0 && (
        <div className="mb-2 text-sm font-semibold text-amber-800" data-testid="print-declarations-pending">
          To confirm at arrival: {pending.map((d) => d.short_label || d.statement).join(", ")}
        </div>
      )}
      <DeclarationsSnapshot snapshot={items} testId="print-declarations" />
      {items.some((d) => d.required) && (
        <div className="mt-2"><ArrivalNotice notice={notice} testId="print-declarations-notice" /></div>
      )}
    </section>
  );
}

export default function BookingPrint() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const view = searchParams.get("view"); // null | "activity"
  const [booking, setBooking] = useState(null);
  const [site, setSite]       = useState(null);
  const [bay, setBay]         = useState(null);
  const [activity, setActivity] = useState(null); // rows for view=activity
  const [err, setErr]         = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase
        .from("lbs_bookings")
        .select("*")
        .eq("id", id)
        .maybeSingle();
      if (cancelled) return;
      if (error) { setErr(error.message); setLoading(false); return; }
      if (!data)  { setErr("Booking not found."); setLoading(false); return; }
      setBooking(data);

      const jobs = [
        data.site_id ? supabase.from("lbs_sites").select("id,name,code,city,state,timezone,operating_hours_start,operating_hours_end").eq("id", data.site_id).maybeSingle() : Promise.resolve({ data: null }),
        data.bay_id  ? supabase.from("lbs_bays") .select("id,name,code,color") .eq("id", data.bay_id).maybeSingle()  : Promise.resolve({ data: null }),
      ];
      if (view === "activity") {
        jobs.push(listBookingActivity(id));
      }
      const results = await Promise.all(jobs);
      if (cancelled) return;
      setSite(results[0]?.data || null);
      setBay(results[1]?.data || null);
      if (view === "activity") {
        setActivity((results[2]?.data || []).slice().sort((a, b) =>
          new Date(a.created_at) - new Date(b.created_at)
        ));
      }
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [id, view]);

  const vis = useFormVisibility(booking?.client_id);
  const visReady = vis.loaded || !booking?.client_id;

  // Auto-open the print dialog once the render (and tenant field visibility) settles.
  useEffect(() => {
    if (!loading && !err && booking && visReady) {
      const t = setTimeout(() => window.print(), 400);
      return () => clearTimeout(t);
    }
  }, [loading, err, booking, visReady]);

  if (loading) {
    return <div className="p-10 text-sm text-slate-500" data-testid="booking-print-loading">Loading booking…</div>;
  }
  if (err) {
    return <div className="p-10 text-sm text-rose-700" data-testid="booking-print-error">{err}</div>;
  }

  const tz = site?.timezone || "Australia/Melbourne";

  if (view === "activity") {
    return (
      <ActivityPrint
        booking={booking}
        site={site}
        bay={bay}
        activity={activity || []}
        tz={tz}
      />
    );
  }

  return (
    <VisCtx.Provider value={vis}>
    <div className="bg-white text-slate-900 min-h-screen">
      {/* On-screen action bar (hidden when printing) */}
      <div className="print:hidden bg-slate-50 border-b border-slate-200 sticky top-0 z-10 px-8 py-3 flex items-center justify-between">
        <div className="text-xs text-slate-500">
          Printable booking · {booking?.reference_number || booking?.id?.slice(0, 8)}
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={() => window.close()} data-testid="print-close-btn">
            Close
          </Button>
          <Button size="sm" onClick={() => window.print()} data-testid="print-again-btn">
            <Printer className="h-4 w-4 mr-1.5" /> Print
          </Button>
        </div>
      </div>

      {/* Printable content */}
      <article className="max-w-4xl mx-auto px-8 py-8 print:p-6" data-testid="booking-print-body">
        {/* Header */}
        <header className="mb-8 pb-4 border-b-2 border-slate-900 flex items-start justify-between gap-6">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Booking record</div>
            <h1 className="text-2xl font-bold tracking-tight mt-1">
              {booking.reference_number || "(Unnumbered)"}
            </h1>
            <div className="text-xs text-slate-500 mt-1">
              {booking.title || "Untitled booking"}
            </div>
          </div>
          <div className="text-right">
            <div className="inline-block text-[10px] uppercase tracking-wider px-2 py-1 rounded border border-slate-400 font-semibold">
              {booking.status?.replace(/_/g, " ") || "unknown"}
            </div>
            <div className="text-[10px] text-slate-500 mt-2">
              Printed {new Date().toLocaleString("en-AU", { timeZone: tz })}
            </div>
          </div>
        </header>

        <Section title="Schedule">
          <Field label="Site">
            {site?.name || NOT_SET}
            {site?.code && <span className="text-slate-400 ml-1">({site.code})</span>}
          </Field>
          <Field label="Bay">
            {bay?.name || NOT_SET}
            {bay?.code && <span className="text-slate-400 ml-1">({bay.code})</span>}
          </Field>
          <Field label="Scheduled start">{fmtDateTime(booking.scheduled_start, tz)}</Field>
          <Field label="Scheduled end">{fmtDateTime(booking.scheduled_end, tz)}</Field>
          <Field label="Direction">{booking.direction}</Field>
          <Field label="Reservation type">{booking.reservation_type || NOT_SET}</Field>
          {site?.operating_hours_start && (
            <Field label="Site hours" wide>
              {site.operating_hours_start} – {site.operating_hours_end} ({site.timezone})
            </Field>
          )}
        </Section>

        <Section title="Sender" tab="goods">
          <Field label="Company" fk="sender_company">{booking.sender_company}</Field>
          <Field label="Contact name" fk="sender_name">{booking.sender_name}</Field>
          <Field label="Phone" fk="sender_phone">{booking.sender_phone}</Field>
          <Field label="Email" fk="sender_email">{booking.sender_email}</Field>
        </Section>

        <Section title="Receiver" tab="goods">
          <Field label="Company" fk="receiver_company">{booking.receiver_company}</Field>
          <Field label="Contact name" fk="receiver_name">{booking.receiver_name}</Field>
          <Field label="Other party notes" wide fk="other_party_contact">{booking.other_party_contact}</Field>
        </Section>

        <Section title="Vehicle & driver">
          <Field label="Vehicle type" slot="vehicle.picklists">{booking.vehicle_type}</Field>
          <Field label="Registrations">{fmtRegos(booking.vehicle_regos)}</Field>
          <Field label="Driver name">{booking.assigned_driver_name || booking.driver_name}</Field>
          <Field label="Driver phone">{booking.driver_phone}</Field>
          <Field label="Driver licence">{booking.driver_licence}</Field>
          <Field label="Licence state">{booking.driver_licence_state}</Field>
          <Field label="Driver TBA">{booking.driver_tba ? "Yes" : "No"}</Field>
          <Field label="Trailer count">{booking.trailer_count ?? NOT_SET}</Field>
        </Section>

        <Section title="Goods">
          <Field label="Description" wide fk="goods_description">{booking.goods_description}</Field>
          <Field label="Pallet count">{booking.pallet_count ?? NOT_SET}</Field>
          <Field label="Weight (kg)" fk="weight">{booking.weight_kg ?? NOT_SET}</Field>
          <Field label="Temperature" slot="qa.temperatures">{booking.temperature_c != null ? `${booking.temperature_c} °C` : NOT_SET}</Field>
          <Field label="Dangerous goods" fk="dangerous_goods">{booking.is_dangerous_goods ? "Yes" : "No"}</Field>
          <Field label="PO number" fk="po_number">{booking.po_number}</Field>
          <Field label="External ref" fk="external_ref">{booking.external_ref}</Field>
        </Section>

        <Section title="Compliance">
          <Field label="Approval required">{booking.requires_approval ? "Yes" : "No"}</Field>
          <Field label="Approved by">{booking.approved_by_user_id ? "System" : NOT_SET}</Field>
          <Field label="Approved at">{fmtDateTime(booking.approved_at, tz)}</Field>
          <Field label="Actual arrival">{fmtDateTime(booking.actual_arrival, tz)}</Field>
          <Field label="Actual departure">{fmtDateTime(booking.actual_departure, tz)}</Field>
          <Field label="Cancelled at">{fmtDateTime(booking.cancelled_at, tz)}</Field>
        </Section>

        <DeclarationsSection booking={booking} />

        {(booking.notes || booking.internal_notes) && (
          <Section title="Notes">
            <Field label="Public notes" wide>{booking.notes}</Field>
            <Field label="Internal notes" wide>{booking.internal_notes}</Field>
          </Section>
        )}

        <Section title="Audit">
          <Field label="Created">{fmtDateTime(booking.created_at, tz)}</Field>
          <Field label="Last updated">{fmtDateTime(booking.updated_at, tz)}</Field>
          <Field label="Booking ID" wide>
            <code className="text-[11px] font-mono text-slate-600">{booking.id}</code>
          </Field>
        </Section>

        <footer className="mt-10 pt-4 border-t border-slate-200 text-[10px] text-slate-400 flex justify-between">
          <span>timeslot.au · logistics booking record</span>
          <span>{booking.reference_number || booking.id?.slice(0, 8)} · {fmtDate(new Date().toISOString(), tz)}</span>
        </footer>
      </article>

      {/* Print-specific tweaks */}
      <style>{`
        @media print {
          .avoid-break { break-inside: avoid; page-break-inside: avoid; }
          @page { margin: 14mm; size: A4; }
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        }
      `}</style>
    </div>
    </VisCtx.Provider>
  );
}


/**
 * ActivityPrint — paper-friendly Activity log for a single booking.
 *
 * Purpose: hand a forklift operator a printed handoff sheet when they
 * cannot practically use the electronic checklist. Renders the audit
 * log as a chronological table with a header block for the booking
 * reference + goods summary and a signature strip at the bottom.
 *
 * Route: /bookings/:id/print?view=activity
 */
function ActivityPrint({ booking, site, bay, activity, tz }) {
  return (
    <div className="bg-white text-slate-900 min-h-screen">
      {/* On-screen action bar */}
      <div className="print:hidden bg-slate-50 border-b border-slate-200 sticky top-0 z-10 px-8 py-3 flex items-center justify-between">
        <div className="text-xs text-slate-500">
          Activity log · {booking?.reference_number || booking?.id?.slice(0, 8)}
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={() => window.close()} data-testid="print-close-btn">
            Close
          </Button>
          <Button size="sm" onClick={() => window.print()} data-testid="print-again-btn">
            <Printer className="h-4 w-4 mr-1.5" /> Print
          </Button>
        </div>
      </div>

      <article className="max-w-4xl mx-auto px-8 py-8 print:p-6" data-testid="booking-activity-print-body">
        {/* Header */}
        <header className="mb-6 pb-3 border-b-2 border-slate-900">
          <div className="flex items-start justify-between gap-6">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">
                Activity log · load handoff sheet
              </div>
              <h1 className="text-2xl font-bold tracking-tight mt-1">
                {booking.reference_number || "(Unnumbered)"}
              </h1>
            </div>
            <div className="text-right">
              <div className="inline-block text-[10px] uppercase tracking-wider px-2 py-1 rounded border border-slate-400 font-semibold">
                {booking.status?.replace(/_/g, " ") || "unknown"}
              </div>
              <div className="text-[10px] text-slate-500 mt-2">
                Printed {new Date().toLocaleString("en-AU", { timeZone: tz })}
              </div>
            </div>
          </div>

          {/* Summary strip */}
          <div className="grid grid-cols-4 gap-4 mt-4 text-sm">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-medium">Site / Bay</div>
              <div className="mt-0.5">
                {site?.name || "—"}
                {bay?.name && <span className="text-slate-500"> · {bay.name}</span>}
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-medium">Direction</div>
              <div className="mt-0.5 capitalize">{booking.direction || "—"}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-medium">Scheduled</div>
              <div className="mt-0.5 tabular-nums">
                {new Date(booking.scheduled_start).toLocaleString("en-AU", {
                  timeZone: tz, day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", hour12: false,
                })}
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-medium">Vehicle / Regos</div>
              <div className="mt-0.5">
                {booking.vehicle_type || "—"}
                <div className="text-[11px] text-slate-500 mt-0.5">
                  {formatRegosShort(booking.vehicle_regos)}
                </div>
              </div>
            </div>
          </div>

          {/* Goods summary */}
          <div className="mt-4 text-sm bg-slate-50 border border-slate-200 rounded px-3 py-2">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-medium">Goods</div>
            <div className="mt-0.5">
              {booking.goods_description || "—"}
              {booking.pallet_count != null && (
                <span className="text-slate-500"> · {booking.pallet_count} pallets</span>
              )}
              {booking.weight_kg != null && (
                <span className="text-slate-500"> · {booking.weight_kg} kg</span>
              )}
              {booking.is_dangerous_goods && (
                <span className="ml-2 inline-block text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border border-red-400 text-red-700 bg-red-50 font-semibold">
                  Dangerous goods
                </span>
              )}
            </div>
          </div>
        </header>

        {/* Activity timeline as a table (paper-friendly) */}
        <section className="avoid-break">
          <h2 className="text-[11px] uppercase tracking-widest text-slate-700 font-semibold mb-2">
            Activity timeline
          </h2>
          {activity.length === 0 ? (
            <div className="text-sm text-slate-500 py-4">No activity recorded.</div>
          ) : (
            <table className="w-full text-sm border-collapse" data-testid="activity-print-table">
              <thead>
                <tr className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold border-b border-slate-300">
                  <th className="text-left py-1.5 pr-3 w-40">When</th>
                  <th className="text-left py-1.5 pr-3">Event</th>
                  <th className="text-left py-1.5 pr-3">By</th>
                  <th className="text-left py-1.5">Notes</th>
                </tr>
              </thead>
              <tbody>
                {activity.map((r) => (
                  <ActivityRowPrint key={r.id} row={r} tz={tz} />
                ))}
              </tbody>
            </table>
          )}
        </section>

        {/* Signature strip — for physical handoff */}
        <section className="mt-10 avoid-break">
          <h2 className="text-[11px] uppercase tracking-widest text-slate-700 font-semibold mb-3">
            Handoff acceptance
          </h2>
          <div className="grid grid-cols-2 gap-8 text-sm">
            <SignatureBlock label="Forklift / dock operator" />
            <SignatureBlock label="Driver" />
          </div>
        </section>

        <footer className="mt-10 pt-3 border-t border-slate-200 text-[10px] text-slate-400 flex justify-between">
          <span>timeslot.au · activity log</span>
          <span>{booking.reference_number || booking.id?.slice(0, 8)}</span>
        </footer>
      </article>

      <style>{`
        @media print {
          .avoid-break { break-inside: avoid; page-break-inside: avoid; }
          @page { margin: 14mm; size: A4; }
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          table { page-break-inside: auto; }
          tr    { page-break-inside: avoid; page-break-after: auto; }
        }
      `}</style>
    </div>
  );
}

function ActivityRowPrint({ row, tz }) {
  const m = /^status:([a-z_]+)->([a-z_]+)$/.exec(row.action || "");
  const fromMeta = m ? BOOKING_STATUSES.find((s) => s.value === m[1]) : null;
  const toMeta   = m ? BOOKING_STATUSES.find((s) => s.value === m[2]) : null;
  const actor    = row.user
    ? [row.user.first_name, row.user.last_name].filter(Boolean).join(" ") || row.user.email
    : (row.user_id ? "Unknown user" : "System");
  const reason   = row.new_values?.reason;
  const when     = row.created_at
    ? new Date(row.created_at).toLocaleString("en-AU", {
        timeZone: tz, day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", hour12: false,
      })
    : "—";

  return (
    <tr className="border-b border-slate-100 align-top">
      <td className="py-1.5 pr-3 tabular-nums text-slate-700">{when}</td>
      <td className="py-1.5 pr-3">
        {m ? (
          <span>
            <span className="text-slate-500">{fromMeta?.label || m[1]}</span>
            <span className="text-slate-400 mx-1">→</span>
            <span className="font-medium">{toMeta?.label || m[2]}</span>
          </span>
        ) : (
          <span className="font-medium">{row.action}</span>
        )}
      </td>
      <td className="py-1.5 pr-3 text-slate-700">{actor}</td>
      <td className="py-1.5 text-slate-700">{reason || <span className="text-slate-400">—</span>}</td>
    </tr>
  );
}

function SignatureBlock({ label }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-medium mb-1">
        {label}
      </div>
      <div className="border-b border-slate-400 h-10" />
      <div className="grid grid-cols-2 gap-4 mt-1 text-[10px] text-slate-500">
        <div>Printed name</div>
        <div>Date / time</div>
      </div>
      <div className="grid grid-cols-2 gap-4 mt-6 text-[10px] text-slate-500">
        <div className="border-b border-slate-400 h-6" />
        <div className="border-b border-slate-400 h-6" />
      </div>
    </div>
  );
}

function formatRegosShort(v) {
  if (!v) return "—";
  if (typeof v === "string") return v || "—";
  if (Array.isArray(v)) return v.filter(Boolean).join(", ") || "—";
  if (typeof v === "object") {
    const parts = [v.prime_mover, v.trailer_1, v.trailer_2].filter(Boolean);
    return parts.length ? parts.join(" / ") : "—";
  }
  return String(v);
}
